import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faCheck } from "@fortawesome/pro-regular-svg-icons";

import { TICKET_AUTO_CLOSE_AFTER_DAYS, TicketUserRef } from "@signalist/shared-types";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";

import { ticketUserFirstName } from "../utils/ticketThread";

interface TicketResolvedBannerProps {
	resolvedBy: TicketUserRef | null;
	isConfirming: boolean;
	onConfirm: () => void;
	onReply: () => void;
}

/** Shown only in RESOLVED (PREC-3938 decision 3): confirm closes, "Not yet" focuses the composer. */
export const TicketResolvedBanner: FunctionComponent<TicketResolvedBannerProps> = ({
	resolvedBy,
	isConfirming,
	onConfirm,
	onReply,
}) => {
	const { t } = useTranslation();

	return (
		<div className="flex items-center gap-3 rounded-xl border border-status-success-light bg-status-success-light px-4 py-3">
			<span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-status-success">
				<Icon icon={faCheck} />
			</span>
			<div className="flex min-w-0 flex-1 flex-col">
				<span className="text-sm font-semibold text-text">
					{resolvedBy
						? t("support.detail.banner.title", { name: ticketUserFirstName(resolvedBy) })
						: t("support.detail.banner.titleAnonymous")}
				</span>
				<span className="text-xs text-text-meta">
					{t("support.detail.banner.body", { days: TICKET_AUTO_CLOSE_AFTER_DAYS })}
				</span>
			</div>
			<Button
				isLoading={isConfirming}
				size={ButtonSize.XS}
				title={t("support.detail.banner.confirm")}
				type="button"
				onClick={onConfirm}
			/>
			<Button
				color={ButtonColor.ACTION_SECONDARY}
				size={ButtonSize.XS}
				title={t("support.detail.banner.reply")}
				type="button"
				onClick={onReply}
			/>
		</div>
	);
};
